/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { memo, useEffect, useState } from 'react';
import { Chart } from '@scuf/charts';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../locales/en-US/descriptors';
import { shouldNotUpdateStackedChart } from '../Utils/Utils';
import './StackedColumnChartTheme.scss';
import { MAGIC_VALUE, StackedColumnConstants } from '../Utils/Constants';
import { IAny } from '../../DashboardKPIWidgets/IEnergyManagementProps';
import { EnergyUsageDataSourceType } from '../Enums';
export interface FuelData {
  name: string;
  color: string;
  actual: (number | null)[];
  previous: (number | null)[];
}
interface IStackedChartData {
  MonthIndex: string[];
  YearIndex: string[];
  fuel: FuelData[];
  SiteWithDataCount?: IAny;
  totalSiteCount?: IAny;
}
export interface IStackedColumnProps {
  unit: string;
  isAllDataAvailable: boolean;
  chartData: IStackedChartData;
  stackType?: string;
  intervalType?: string;
  renderedFromCarbon?: boolean;
  dataSourceType?: string;
  isSite?: boolean;
}
interface ITooltipDetails {
  label: string;
  x: number;
  y: number;
  high?: number;
  low?: number;
  pointValue: number;
  xCategory?: string;
  yCategory?: string;
  colorIndex: number;
}
const stackTypeCheck = (stackType: string | undefined) =>
  stackType !== 'nilmDisaggreagtion' ? '#1272B2' : '';

const stackTypeCOTwoCheck = (stackType: string | undefined, t: IAny) => {
  const d = descriptors.energyManagement;
  if (stackType !== 'nilmDisaggreagtion') {
    return t(d.CO2);
  } else {
    return '';
  }
};

const stackTypeActualTotalConsumptionCheck = (
  stackType: string | undefined,
  t: IAny,
  renderedFromCarbon?: boolean
) => {
  const d = descriptors.energyManagement;
  if (stackType !== 'nilmDisaggreagtion') {
    return renderedFromCarbon ? t(d.ACTUALEMISSIONS) : t(d.ACTUAL);
  } else {
    return t(d.TOTALCONSUMPTION);
  }
};
const StackedColumnChart = memo((props: IStackedColumnProps) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const { unit, chartData, stackType, intervalType, isSite } = props;
  const {
    MonthIndex,
    YearIndex,
    SiteWithDataCount,
    totalSiteCount
  } = chartData;
  const getDefaultFuelData = () =>
    chartData.fuel.map((fuel) => {
      return { ...fuel, selected: true };
    });

  const [fuelData, setfuelData] = useState(
    JSON.parse(JSON.stringify(getDefaultFuelData()))
  );

  useEffect(() => {
    setfuelData(getDefaultFuelData());
  }, [props.chartData]);

  const {
    actualColumnWidth,
    previousColumnWidth,
    numberOfGridLines,
    stackBorder,
    groupPadding,
    actualOpacity,
    previousOpacity
  } = StackedColumnConstants;

  const YAxisProps = {
    allowDecimals: true,
    gridLineColor: '#505050',
    gridLineWidth: 1,
    className: 'chart-widget-yaxis',
    tickAmount: numberOfGridLines,
    labelSuffix: `  ${unit}`
  };
  const commonProps = {
    groupPadding: groupPadding,
    stacking: 'normal' as IAny,
    borderWidth: stackBorder
  };
  const stackes: { key: string; element: JSX.Element }[] = [];
  fuelData.forEach((fuel: IAny, index: number) => {
    if (fuel.selected && fuel.name !== 'TotalConsuption') {
      const baselineElement = (
        <Chart.Column
          key={fuel.name + `previous`}
          opacity={previousOpacity}
          name={fuel.name + `previous`}
          data={fuel.previous}
          color={fuel.color}
          stack={t(d.PREVIOUSPERIOD)}
          // pointWidth={previousColumnWidth}
          {...commonProps}
        />
      );
      {
        stackType !== 'nilmDisaggreagtion' &&
          stackes.push({
            key: `baseline-index-${index}`,
            element: baselineElement
          });
      }
      const actualElement = (
        <Chart.Column
          key={fuel.name + `actual`}
          opacity={actualOpacity}
          name={fuel.name + `actual`}
          data={fuel.actual}
          color={fuel.color}
          stack={t(d.ACTUAL)}
          // pointWidth={actualColumnWidth}
          {...commonProps}
        />
      );
      stackes.push({
        key: `actual-index-${index}`,
        element: actualElement
      });
    }
  });

  // to get the fuel index which has value for the current month
  const getFuelIndex = (fuelData: any, index: number) => {
    return fuelData.findIndex((fuel: any) => {
      return (
        typeof fuel.actual[index] === 'number' ||
        typeof fuel.previous[index] === 'number'
      );
    });
  };

  const renderTooltip = (details: ITooltipDetails) => {
    const { x } = details;
    const tooltipMonth = MonthIndex[x];
    const tooltipYear = YearIndex[x];
    const siteWithDataCount: any =
      SiteWithDataCount && typeof SiteWithDataCount === 'object'
        ? SiteWithDataCount[x]
        : SiteWithDataCount ?? null;
    if (
      fuelData.length > 0 &&
      details.label.includes('actual') &&
      details.label.includes(
        fuelData.filter((fuel: any) => fuel.selected)[
          getFuelIndex(fuelData, x) !== -1 ? getFuelIndex(fuelData, x) : 0
        ].name
      )
    )
      return (
        <div className="emaas-chart-widget-container">
          <div className="tooltip-top">
            <div>
              <span className="tooltip-date emaasThemeColor">
                {tooltipMonth}{' '}
                {stackType === 'nilmDisaggreagtion' &&
                  intervalType !== 'monthly' &&
                  tooltipYear}
              </span>
            </div>
            <div className="tooltip-pane">
              {fuelData.filter((fuel: any) => fuel.selected).length > 1 && (
                <div className="tooltip-pane-item top-margin">
                  <div className="tooltip-heading-container">
                    {
                      <span
                        className="dot"
                        style={{
                          backgroundColor: stackTypeCheck(stackType)
                        }}
                      ></span>
                    }
                    <span className="tooltip-header-label emaasThemeColor">
                      {stackTypeCOTwoCheck(stackType, t)}
                      {stackTypeActualTotalConsumptionCheck(
                        stackType,
                        t,
                        props.renderedFromCarbon
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="tooltip-header-value emaasThemeColor">
                      {stackType !== 'nilmDisaggreagtion' &&
                        fuelData
                          .filter((fuel: any) => fuel.selected)
                          .reduce(
                            (prev: IAny, curr: IAny) =>
                              prev + (curr?.actual[x] || 0),
                            0
                          )
                          .toFixed(2)}{' '}
                      {stackType === 'nilmDisaggreagtion' &&
                        fuelData
                          .filter(
                            (val: IAny) =>
                              val.name === 'TotalConsuption' && val.selected
                          )
                          .map((displayVal: IAny) => displayVal.actual[x])}
                      {unit}
                    </span>
                  </div>
                </div>
              )}
              {stackType !== 'nilmDisaggreagtion'
                ? fuelData.filter((fuel: any) => fuel.selected).length > 1 && (
                    <div className="tooltip-pane-item">
                      <div className="tooltip-heading-container">
                        <span
                          className="dot"
                          style={{ backgroundColor: '#606060' }}
                        ></span>
                        <span className="tooltip-header-label emaasThemeColor">
                          {props.renderedFromCarbon
                            ? t(d.PREVIOUSEMISSIONS)
                            : t(d.PREVIOUSPERIOD)}
                        </span>
                      </div>
                      <div>
                        <span className="tooltip-header-value emaasThemeColor">
                          {fuelData
                            .filter((fuel: any) => fuel.selected)
                            .reduce((prev: IAny, curr: IAny) => {
                              return (
                                prev +
                                (curr?.previous[x] === null
                                  ? MAGIC_VALUE.ZERO
                                  : curr?.previous[x] || MAGIC_VALUE.ZERO)
                              );
                            }, MAGIC_VALUE.ZERO)
                            .toFixed(MAGIC_VALUE.TWO)}{' '}
                          {unit}
                        </span>
                      </div>
                    </div>
                  )
                : ''}
              {fuelData
                .filter((fuel: any) => fuel.selected)
                .map((fuel: IAny) => {
                  if (fuel.name !== 'TotalConsuption')
                    return (
                      <>
                        <div className="tooltip-pane-item top-margin">
                          <div className="tooltip-heading-container">
                            <span
                              className="dot"
                              style={{ backgroundColor: fuel.color }}
                            ></span>
                            <span className="tooltip-header-label emaasThemeColor">
                              {fuel.name}{' '}
                              {props.renderedFromCarbon
                                ? t(d.ACTUALEMISSIONS)
                                : t(d.ACTUAL)}
                            </span>
                          </div>
                          <div>
                            <span className="tooltip-header-value emaasThemeColor">
                              {fuel.actual[x] === null
                                ? MAGIC_VALUE.ZERO.toFixed(MAGIC_VALUE.TWO)
                                : fuel.actual[x]?.toFixed(MAGIC_VALUE.TWO)}{' '}
                              {unit}
                            </span>
                          </div>
                        </div>
                        {stackType !== 'nilmDisaggreagtion' && (
                          <div className="tooltip-pane-item">
                            <div className="tooltip-heading-container">
                              <span
                                className="dot"
                                style={{ backgroundColor: '#606060' }}
                              ></span>
                              <span className="tooltip-header-label emaasThemeColor">
                                {props.renderedFromCarbon
                                  ? t(d.PREVIOUSEMISSIONS)
                                  : t(d.PREVIOUSPERIOD)}
                              </span>
                            </div>
                            <div>
                              <span className="tooltip-header-value emaasThemeColor">
                                {fuel.previous[x] === null
                                  ? MAGIC_VALUE.ZERO.toFixed(MAGIC_VALUE.TWO)
                                  : fuel.previous[x]?.toFixed(
                                      MAGIC_VALUE.TWO
                                    )}{' '}
                                {unit}
                              </span>
                            </div>
                          </div>
                        )}
                      </>
                    );
                })}
            </div>
          </div>
          {props.renderedFromCarbon &&
          !isSite &&
          props.dataSourceType === EnergyUsageDataSourceType.UTILITY_METER ? (
            <div className="tooltip-pane-item">
              <p className="basic-site-with-data-count-msg">
                {t(d.CARBONUTILITYGRAPHTOOLTIP, {
                  siteWithDataCount,
                  totalSiteCount
                })}
              </p>
            </div>
          ) : (
            ''
          )}
        </div>
      );
  };

  const showSelectedFuel = (fuelName: string) => {
    const allSelected = fuelData.every((fuel: IAny) => fuel.selected);
    if (allSelected)
      setfuelData(
        fuelData.map((fuel: IAny) => {
          if (fuel.name === fuelName) {
            return { ...fuel, selected: true };
          } else {
            return { ...fuel, selected: false };
          }
        })
      );
    else setfuelData(getDefaultFuelData());
  };

  return (
    <div
      className={
        fuelData.length <= 6
          ? 'emaas-stacked-column-chart'
          : 'emaas-stacked-column-chart nilm-with-multiple-disaggregation'
      }
    >
      <div className="chart-widget-container">
        <Chart showLegend={false} height={fuelData.length > 6 ? 260 : ''}>
          {stackes.map((itm) => itm.element)}
          <Chart.Tooltip
            borderRadius={10}
            enabled={true}
            shared={true}
            renderContent={renderTooltip}
            backgroundColor="white"
          />
          <Chart.XAxis categories={MonthIndex} className="month-labels" />
          <Chart.YAxis {...YAxisProps} />
        </Chart>
      </div>
      <div
        className={
          fuelData.length <= 6
            ? 'flex-container-legends'
            : 'flex-container-more-legends'
        }
      >
        {fuelData.map((fuel: IAny, index: number) => {
          if (fuel.name !== 'TotalConsuption')
            return (
              <div
                style={{
                  borderLeft: `0.2rem solid ${fuel.color}`,
                  opacity: `${fuel.selected ? 1 : 0.5}`
                }}
                className={
                  fuelData.length <= 6
                    ? `fuel-legend legend-text`
                    : `legend-custom-name`
                }
                onClick={() => showSelectedFuel(fuel.name)}
                key={`stacked-chart-legend-${index}`}
              >
                {fuel.name}
              </div>
            );
        })}
      </div>
    </div>
  );
}, shouldNotUpdateStackedChart);

export default StackedColumnChart;
